// src/components/notifications/Notification.js
import React, { useState, useEffect, useCallback } from 'react';
import './Notification.css'; // Ensure this CSS file is updated for new button styles

const Notification = ({ message, type = 'info', duration, icon, onClose, isPersistent = false, actions = [] }) => {
  const [visible, setVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  // Determine the effective duration. An explicitly passed duration prop always wins.
  // Otherwise, we set a default based on the notification type.
  const getEffectiveDuration = () => {
    if (duration !== undefined && duration !== null) {
      return duration;
    }
    switch (type) {
      case 'success':
      case 'info':
        return 3000; // Shorter timeout for success messages
      case 'error':
      case 'warning':
      default:
        return 7000; // Longer timeout for messages requiring more attention
    }
  };
  const effectiveDuration = getEffectiveDuration();

  useEffect(() => {
    if (message) {
      // Log error messages to the console for easier debugging
      if (type === 'error') {
        const cleanMessage = message.replace(/<[^>]*>?/gm, ''); // Strip HTML for a cleaner log
        console.error(`Notification Error: ${cleanMessage}`);
      }
      setVisible(true);

      // Only set a timer if the notification is NOT persistent
      if (!isPersistent) {
        let animationFrameId;
        // Explicitly set the initial timeLeft when the effect runs for a new message.
        // This prevents the old state from showing during a re-render.
        setTimeLeft(Math.ceil(effectiveDuration / 1000));
        const startTime = performance.now();

        const updateCountdown = (currentTime) => {
          const elapsedTime = currentTime - startTime;
          const remainingTime = Math.max(0, effectiveDuration - elapsedTime);
          const newTimeLeft = Math.ceil(remainingTime / 1000);
          // A direct set is simpler and safer here than a conditional one.
          setTimeLeft(newTimeLeft);

          if (remainingTime > 0) {
            animationFrameId = requestAnimationFrame(updateCountdown);
          }
        };

        animationFrameId = requestAnimationFrame(updateCountdown);

        const timer = setTimeout(() => {
          setVisible(false);
          if (onClose) onClose();
        }, effectiveDuration);
        return () => {
          clearTimeout(timer);
          cancelAnimationFrame(animationFrameId);
        };
      }
    } else {
      setVisible(false);
    }
  }, [message, type, effectiveDuration, onClose, isPersistent]);

  const handleActionClick = useCallback(
    (actionOnClick) => {
      if (actionOnClick) {
        actionOnClick();
      }
      if (onClose) {
        // Always close the notification after an action
        onClose();
      }
    },
    [onClose]
  );

  // Effect to handle 'Enter' key press for the primary action
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault(); // Prevent default form submission
        const primaryAction = actions.find((action) => action.className === 'primary');
        if (primaryAction) {
          handleActionClick(primaryAction.onClick);
        } else if (isPersistent && actions.length === 0) {
          handleActionClick(null); // Trigger the default "OK" action
        }
      }
    };
    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, actions, isPersistent, handleActionClick]);

  if (!visible || !message) {
    return null;
  }

  const defaultIcons = {
    success: <i className="fas fa-check-circle"></i>,
    error: <i className="fas fa-times-circle"></i>,
    warning: <i className="fas fa-exclamation-triangle"></i>,
    info: <i className="fas fa-info-circle"></i>,
  };

  return (
    <div className={`notification notification-${type} ${visible ? 'show' : ''}`}>
      <div className="notification-content">
        <div className="notification-icon-wrapper">
          {!isPersistent && (
            <svg className="notification-timer-svg" width="36" height="36" viewBox="0 0 36 36">
              <circle className="notification-timer-circle-bg" cx="18" cy="18" r="16" />
              <circle className="notification-timer-circle" style={{ animationDuration: `${effectiveDuration}ms` }} cx="18" cy="18" r="16" />
            </svg>
          )}
          <div className="notification-icon-inner">{!isPersistent ? <span className="notification-timer-text">{timeLeft}</span> : icon || defaultIcons[type]}</div>
        </div>
        <span className="notification-message" dangerouslySetInnerHTML={{ __html: message }}></span>
      </div>
      {/* Show actions if they exist, or show a default OK if persistent with no actions */}
      {(actions && actions.length > 0) || isPersistent ? (
        <div className="notification-actions">
          {actions && actions.length > 0 ? (
            actions.map((action, index) => (
              <button key={index} onClick={() => handleActionClick(action.onClick)} className={`notification-action-button ${action.className || ''}`}>
                {action.text}
              </button>
            ))
          ) : (
            // This part only runs if isPersistent is true and actions is empty
            <button onClick={() => handleActionClick(null)} className="notification-action-button">
              OK
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default Notification;
